/**
 * Smart Guides
 * Shows alignment guides when dragging layers near snap points
 */

import React from 'react';

export interface Guide {
  type: 'horizontal' | 'vertical';
  position: number; // x for vertical, y for horizontal
  start: number;    // start of the line
  end: number;      // end of the line
  label?: string;   // optional label (e.g., "50%")
}

interface SmartGuidesProps {
  guides: Guide[];
  zoom: number;
  panOffset: { x: number; y: number };
  canvasOffset: { x: number; y: number };
}

export function SmartGuides({ guides, zoom, panOffset, canvasOffset }: SmartGuidesProps) {
  if (guides.length === 0) return null;

  return (
    <svg
      className="absolute inset-0 pointer-events-none z-40"
      style={{ overflow: 'visible' }}
    >
      {guides.map((guide, index) => {
        // Transform guide coordinates to screen coordinates
        const transformX = (x: number) => canvasOffset.x + panOffset.x + x * zoom;
        const transformY = (y: number) => canvasOffset.y + panOffset.y + y * zoom;

        if (guide.type === 'vertical') {
          const x = transformX(guide.position);
          const y1 = transformY(guide.start);
          const y2 = transformY(guide.end);

          return (
            <g key={index}>
              <line
                x1={x}
                y1={y1}
                x2={x}
                y2={y2}
                stroke="#F472B6"
                strokeWidth={1}
                strokeDasharray="4 2"
              />
              {guide.label && (
                <text
                  x={x + 4}
                  y={(y1 + y2) / 2}
                  fill="#F472B6"
                  fontSize={10}
                  fontFamily="monospace"
                >
                  {guide.label}
                </text>
              )}
            </g>
          );
        } else {
          const y = transformY(guide.position);
          const x1 = transformX(guide.start);
          const x2 = transformX(guide.end);

          return (
            <g key={index}>
              <line
                x1={x1}
                y1={y}
                x2={x2}
                y2={y}
                stroke="#F472B6"
                strokeWidth={1}
                strokeDasharray="4 2"
              />
              {guide.label && (
                <text
                  x={(x1 + x2) / 2}
                  y={y - 4}
                  fill="#F472B6"
                  fontSize={10}
                  fontFamily="monospace"
                  textAnchor="middle"
                >
                  {guide.label}
                </text>
              )}
            </g>
          );
        }
      })}
    </svg>
  );
}

// Snap threshold in canvas units
const SNAP_THRESHOLD = 5;

interface LayerBounds {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

interface SnapResult {
  guides: Guide[];
  snappedX?: number;
  snappedY?: number;
}

/**
 * Calculate smart guides and snap positions for a moving layer
 */
export function calculateSmartGuides(
  movingLayer: LayerBounds,
  otherLayers: LayerBounds[],
  canvasWidth: number,
  canvasHeight: number
): SnapResult {
  const guides: Guide[] = [];
  let snappedX: number | undefined;
  let snappedY: number | undefined;

  const layerCenterX = movingLayer.x + movingLayer.width / 2;
  const layerCenterY = movingLayer.y + movingLayer.height / 2;
  const layerRight = movingLayer.x + movingLayer.width;
  const layerBottom = movingLayer.y + movingLayer.height;

  // Canvas snap points
  const canvasCenterX = canvasWidth / 2;
  const canvasCenterY = canvasHeight / 2;

  // Check canvas center alignment (vertical guide for horizontal center)
  if (Math.abs(layerCenterX - canvasCenterX) < SNAP_THRESHOLD) {
    snappedX = canvasCenterX - movingLayer.width / 2;
    guides.push({
      type: 'vertical',
      position: canvasCenterX,
      start: 0,
      end: canvasHeight,
      label: '50%',
    });
  }

  // Check canvas center alignment (horizontal guide for vertical center)
  if (Math.abs(layerCenterY - canvasCenterY) < SNAP_THRESHOLD) {
    snappedY = canvasCenterY - movingLayer.height / 2;
    guides.push({
      type: 'horizontal',
      position: canvasCenterY,
      start: 0,
      end: canvasWidth,
      label: '50%',
    });
  }

  // Check canvas edge alignments
  // Left edge
  if (Math.abs(movingLayer.x) < SNAP_THRESHOLD) {
    snappedX = 0;
    guides.push({
      type: 'vertical',
      position: 0,
      start: movingLayer.y,
      end: layerBottom,
    });
  }
  // Right edge
  if (Math.abs(layerRight - canvasWidth) < SNAP_THRESHOLD) {
    snappedX = canvasWidth - movingLayer.width;
    guides.push({
      type: 'vertical',
      position: canvasWidth,
      start: movingLayer.y,
      end: layerBottom,
    });
  }
  // Top edge
  if (Math.abs(movingLayer.y) < SNAP_THRESHOLD) {
    snappedY = 0;
    guides.push({
      type: 'horizontal',
      position: 0,
      start: movingLayer.x,
      end: layerRight,
    });
  }
  // Bottom edge
  if (Math.abs(layerBottom - canvasHeight) < SNAP_THRESHOLD) {
    snappedY = canvasHeight - movingLayer.height;
    guides.push({
      type: 'horizontal',
      position: canvasHeight,
      start: movingLayer.x,
      end: layerRight,
    });
  }

  // Check alignment with other layers
  for (const other of otherLayers) {
    if (other.id === movingLayer.id) continue;

    const otherCenterX = other.x + other.width / 2;
    const otherCenterY = other.y + other.height / 2;
    const otherRight = other.x + other.width;
    const otherBottom = other.y + other.height;

    // Vertical alignments (layer edges and centers)
    const verticalChecks = [
      { moving: movingLayer.x, other: other.x, label: 'left' },
      { moving: movingLayer.x, other: otherCenterX, label: 'left-center' },
      { moving: movingLayer.x, other: otherRight, label: 'left-right' },
      { moving: layerCenterX, other: other.x, label: 'center-left' },
      { moving: layerCenterX, other: otherCenterX, label: 'center' },
      { moving: layerCenterX, other: otherRight, label: 'center-right' },
      { moving: layerRight, other: other.x, label: 'right-left' },
      { moving: layerRight, other: otherCenterX, label: 'right-center' },
      { moving: layerRight, other: otherRight, label: 'right' },
    ];

    for (const check of verticalChecks) {
      if (Math.abs(check.moving - check.other) < SNAP_THRESHOLD) {
        // Calculate snap position
        let newX: number;
        if (check.label.startsWith('left')) {
          newX = check.other;
        } else if (check.label.startsWith('center')) {
          newX = check.other - movingLayer.width / 2;
        } else {
          newX = check.other - movingLayer.width;
        }

        if (snappedX === undefined) snappedX = newX;

        // Add guide line
        const minY = Math.min(movingLayer.y, other.y);
        const maxY = Math.max(layerBottom, otherBottom);
        guides.push({
          type: 'vertical',
          position: check.other,
          start: minY,
          end: maxY,
        });
      }
    }

    // Horizontal alignments (layer edges and centers)
    const horizontalChecks = [
      { moving: movingLayer.y, other: other.y, label: 'top' },
      { moving: movingLayer.y, other: otherCenterY, label: 'top-center' },
      { moving: movingLayer.y, other: otherBottom, label: 'top-bottom' },
      { moving: layerCenterY, other: other.y, label: 'center-top' },
      { moving: layerCenterY, other: otherCenterY, label: 'center' },
      { moving: layerCenterY, other: otherBottom, label: 'center-bottom' },
      { moving: layerBottom, other: other.y, label: 'bottom-top' },
      { moving: layerBottom, other: otherCenterY, label: 'bottom-center' },
      { moving: layerBottom, other: otherBottom, label: 'bottom' },
    ];

    for (const check of horizontalChecks) {
      if (Math.abs(check.moving - check.other) < SNAP_THRESHOLD) {
        // Calculate snap position
        let newY: number;
        if (check.label.startsWith('top')) {
          newY = check.other;
        } else if (check.label.startsWith('center')) {
          newY = check.other - movingLayer.height / 2;
        } else {
          newY = check.other - movingLayer.height;
        }

        if (snappedY === undefined) snappedY = newY;

        // Add guide line
        const minX = Math.min(movingLayer.x, other.x);
        const maxX = Math.max(layerRight, otherRight);
        guides.push({
          type: 'horizontal',
          position: check.other,
          start: minX,
          end: maxX,
        });
      }
    }
  }

  return { guides, snappedX, snappedY };
}
